"use client";

import { useTranslations } from "use-intl";

import { TelegramIcon } from "@/components/icons/telegram-icon";
import { Link } from "@/components/link";
import { getTelegramConnectionButtonLabel } from "@/components/telegram/getTelegramConnectionButtonLabel";
import type { TelegramConnectionState } from "@/components/telegram/telegram-connection-state";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface TelegramCompactConnectionActionProps {
  available: boolean;
  className?: string;
  connecting: boolean;
  connectionState: TelegramConnectionState;
  label: string;
  loading: boolean;
  onConnect: () => Promise<void>;
}

/** Renders a compact Telegram authorization action for conversion surfaces. */
export function TelegramCompactConnectionAction({
  available,
  className,
  connecting,
  connectionState,
  label,
  loading,
  onConnect,
}: TelegramCompactConnectionActionProps) {
  const t = useTranslations("telegram");

  if (!available) {
    return <p className={cn("text-sm text-muted-foreground", className)}>{t("unavailable")}</p>;
  }

  if (connectionState === "alerts-enabled") {
    return (
      <div className={cn("flex flex-wrap items-center gap-3", className)}>
        <div className="inline-flex items-center gap-2 text-sm font-bold text-[#168ac2] dark:text-[#63b9e6]">
          <TelegramIcon className="size-5" />
          {t("connectedTitle")}
        </div>
        <Link
          to="/settings"
          search={{ tab: "notifications" }}
          className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
        >
          {t("settingsCta")}
        </Link>
      </div>
    );
  }

  const recoveringPermission = connectionState === "alerts-disabled";
  const buttonLabel = getTelegramConnectionButtonLabel({
    checkingLabel: t("checkingConnection"),
    connecting,
    connectingLabel: t("connecting"),
    defaultLabel: label,
    loading,
    recoveringLabel: t("enableAlerts"),
    recoveringPermission,
  });

  return (
    <Button
      size="lg"
      aria-busy={loading || connecting}
      className={cn(
        "min-h-12 w-full gap-2 bg-[#229ed9] px-6 font-black text-white shadow-[0_12px_28px_rgba(34,158,217,0.24)] hover:bg-[#168ac2] focus-visible:ring-[#229ed9] sm:w-auto",
        className,
      )}
      disabled={connecting || loading}
      onClick={onConnect}
    >
      <TelegramIcon className="size-5" />
      {buttonLabel}
    </Button>
  );
}
